// Client/src/AssetClassContext.jsx
// Renders high-level asset class & market context based on result.asset_class_context

import React from "react";
import { getReportLabels } from "./reportI18n";

/**
 * Split summary text into paragraphs using blank lines.
 */
function splitSummary(summary) {
  if (!summary) return [];
  return summary
    .split(/\n{2,}/) // split on one or more blank lines
    .map((s) => s.trim())
    .filter(Boolean);
}


function polishDanishMarketContext(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/\bDette markedsoversigt\b/g, "Denne markedsoversigt")
    .replace(/\bCVM har udstedt\b/g, "Comissão de Valores Mobiliários (CVM), Brasiliens værdipapirtilsyn, har udstedt")
    .replace(/\bmodtagelser og obligationstokens\b/gi, "fordringer og fastforrentede tokens")
    .replace(/\btokeniserede modtagelser\b/gi, "tokeniserede fordringer")
    .replace(/\blandbrugsmodtagelsesstruktur\b/gi, "struktur for landbrugsfordringer")
    .replace(/\bmarkedsevolution\b/gi, "markedsudvikling")
    .replace(/\blandbrugsemittenter\b/gi, "udstedere fra landbrugssektoren")
    .replace(/\bomfavnende vækst\b/gi, "præget af vækst")
    .replace(/\baktiver-tokenisering\b/gi, "tokenisering af aktiver");
}


function polishGermanMarketContext(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/^Die CVM hat\b/, "Die Comissão de Valores Mobiliários (CVM), die brasilianische Wertpapieraufsicht, hat")
    .replace(/\bBerichtsausgaben\b/gi, "Berichtsergebnisse")
    .replace(/\brichtungsweise glaubwürdig\b/gi, "in ihrer Grundrichtung plausibel")
    .replace(/\bLieferökonomie\b/gi, "wirtschaftliche Tragfähigkeit der Umsetzung");
}

function polishFrenchMarketContext(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/^La CVM a\b/, "La Comissão de Valores Mobiliários (CVM), l’autorité brésilienne des marchés de valeurs mobilières, a")
    .replace(/\btokens de créances et de revenus fixes\b/gi, "tokens de créances et de titres à revenu fixe")
    .replace(/\bcréances tokenisées\b/gi, "créances tokenisées")
    .replace(/\bdirectionnellement crédible\b/gi, "globalement plausible")
    .replace(/\bdirectionnellement possible\b/gi, "globalement plausible")
    .replace(/\béconomie d’exécution\b/gi, "économie de mise en œuvre");
}

function polishSpanishMarketContext(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(/^La CVM ha\b/, "La Comissão de Valores Mobiliários (CVM), el regulador brasileño del mercado de valores, ha")
    .replace(/\bLa estrategia de receivables\b/gi, "La estrategia basada en cuentas por cobrar")
    .replace(/\brecibibles\b/gi, "cuentas por cobrar")
    .replace(/\brecibos agrícolas\b/gi, "cuentas por cobrar agrícolas")
    .replace(/\brecibos\b/gi, "cuentas por cobrar")
    .replace(/\btokens? de recibos\b/gi, "tokens vinculados a cuentas por cobrar")
    .replace(/\btokens? de recibibles\b/gi, "tokens vinculados a cuentas por cobrar")
    .replace(/\bun tokens vinculados a cuentas por cobrar o de renta fija\b/gi, "un token vinculado a cuentas por cobrar o a instrumentos de renta fija")
    .replace(/\bcuentas por cobrar tokenizados\b/gi, "cuentas por cobrar tokenizadas")
    .replace(/\bcuentas por cobrar brasileños\b/gi, "cuentas por cobrar brasileñas")
    .replace(/\bcuentas por cobrar agrícolas brasileños\b/gi, "cuentas por cobrar agrícolas brasileñas")
    .replace(/\bdireccionalmente creíble\b/gi, "plausible en términos generales")
    .replace(/\bdireccionalmente posible\b/gi, "plausible en términos generales")
    .replace(/\beconomía de entrega\b/gi, "viabilidad económica de la implementación")
    .replace(/\ba los cuentas por cobrar\b/gi, "a las cuentas por cobrar")
    .replace(/\brecibibles agrícolas brasileños\b/gi, "cuentas por cobrar agrícolas brasileñas")
    .replace(/\brecibibles brasileños\b/gi, "cuentas por cobrar brasileñas")
    .replace(/\brecibibles\b/gi, "cuentas por cobrar")
    .replace(/\ba los recibos\b/gi, "a cuentas por cobrar")
    .replace(/\bde los recibos\b/gi, "de las cuentas por cobrar");
}

function localizeMarketContextText(value, reportLanguageCode) {
  if (reportLanguageCode === "da") return polishDanishMarketContext(value);
  if (reportLanguageCode === "de") return polishGermanMarketContext(value);
  if (reportLanguageCode === "fr") return polishFrenchMarketContext(value);
  if (reportLanguageCode === "es") return polishSpanishMarketContext(value);
  return value;
}

function isSubstantiveMarketPhase(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  const lower = text.toLowerCase();

  // Some generated reports contain a provenance caveat in the market_phase field
  // rather than an actual market-stage assessment (for example: "the market phase
  // is described solely from activated and confirmed source statements"). Rendering
  // that under "Current market phase" is tautological and adds no decision value.
  const provenanceOnlySignals = [
    "solely described", "activated and confirmed", "activated verified",
    "udelukkende beskrevet", "beskrives udelukkende", "aktiverede og bekræftede", "aktiverede, verificerede", "markedsrelaterede udsagn",
    "ausschließlich", "aktivierten und bestätigten",
    "uniquement décrite", "sources activées",
    "exclusivamente descrita", "fuentes activadas",
    "仅根据", "已启用", "经确认",
    "exclusivamente descrita", "fontes ativadas",
  ];
  if (provenanceOnlySignals.some((signal) => lower.includes(signal))) return false;
  return true;
}

export default function AssetClassContext({ assetClassContext, reportLanguageCode = "en" }) {
  if (!assetClassContext || typeof assetClassContext !== "object") {
    return null;
  }

  const {
    summary,
    market_phase,
    strengths,
    weaknesses,
    data_confidence,
  } = assetClassContext;

  const hasAnyContent =
    (summary && summary.trim().length > 0) ||
    (market_phase && market_phase.trim().length > 0) ||
    (Array.isArray(strengths) && strengths.length > 0) ||
    (Array.isArray(weaknesses) && weaknesses.length > 0);

  if (!hasAnyContent) {
    return null;
  }

  const labels = getReportLabels(reportLanguageCode);
  const summaryParagraphs = splitSummary(summary).map((p) => localizeMarketContextText(p, reportLanguageCode));
  const strengthsList = Array.isArray(strengths) ? strengths.map((x) => localizeMarketContextText(x, reportLanguageCode)) : [];
  const weaknessesList = Array.isArray(weaknesses) ? weaknesses.map((x) => localizeMarketContextText(x, reportLanguageCode)) : [];

  const showMarketPhase = isSubstantiveMarketPhase(market_phase);
  const implicationCopy = {
    en: {
      bridgeTitle: "What this context means for the case",
      bridgeBody: "Against this market backdrop, the points below show which features of the proposed structure support the value case and which still constrain it.",
      strengths: "Structural factors supporting the case",
      weaknesses: "Structural factors that still constrain the case",
    },
    da: { bridgeTitle: "Hvad konteksten betyder for casen", bridgeBody: "På denne markedsbaggrund viser punkterne nedenfor, hvilke dele af den foreslåede struktur der styrker værdicasen, og hvilke der fortsat begrænser den.", strengths: "Strukturelle forhold der styrker casen", weaknesses: "Strukturelle forhold der fortsat begrænser casen" },
    de: { bridgeTitle: "Was dieser Kontext für den Fall bedeutet", bridgeBody: "Vor diesem Markthintergrund zeigen die folgenden Punkte, welche Merkmale den Mehrwertfall stützen und welche ihn weiterhin begrenzen.", strengths: "Strukturelle Faktoren, die den Fall stützen", weaknesses: "Strukturelle Faktoren, die den Fall weiterhin begrenzen" },
    fr: { bridgeTitle: "Ce que ce contexte signifie pour le cas", bridgeBody: "Dans ce contexte de marché, les points ci-dessous montrent les caractéristiques qui soutiennent le cas de valeur et celles qui le limitent encore.", strengths: "Facteurs structurels qui soutiennent le cas", weaknesses: "Facteurs structurels qui limitent encore le cas" },
    es: { bridgeTitle: "Qué significa este contexto para el caso", bridgeBody: "En este contexto de mercado, los puntos siguientes muestran qué características respaldan el caso de valor y cuáles todavía lo limitan.", strengths: "Factores estructurales que respaldan el caso", weaknesses: "Factores estructurales que aún limitan el caso" },
    "pt-BR": { bridgeTitle: "O que este contexto significa para o caso", bridgeBody: "Diante desse contexto de mercado, os pontos abaixo mostram quais características sustentam o caso de valor e quais ainda o limitam.", strengths: "Fatores estruturais que sustentam o caso", weaknesses: "Fatores estruturais que ainda limitam o caso" },
    "zh-Hans": { bridgeTitle: "这一背景对本案例意味着什么", bridgeBody: "在这一市场背景下，下列要点说明哪些结构特征支持该价值案例，以及哪些因素仍构成约束。", strengths: "支持本案例的结构性因素", weaknesses: "仍限制本案例的结构性因素" },
  };
  const implication = implicationCopy[reportLanguageCode] || implicationCopy.en;

  const confidenceLabel =
    data_confidence === "high"
      ? "high"
      : data_confidence === "medium"
      ? "medium"
      : data_confidence === "low"
      ? "low"
      : null;

  return (
    <section className="card asset-class-context-card" style={{ marginBottom: "24px" }}>
      <div className="publication-chapter-heading publication-chapter-heading--major">
        <span className="publication-chapter-number">02</span>
        <span className="publication-chapter-divider" aria-hidden="true" />
        <h3>{labels.assetClassContext}</h3>
      </div>
      <div
        className="asset-class-context-panel"
        style={{
          borderRadius: "12px",
          padding: "16px 20px",
          background: "rgba(99, 102, 241, 0.06)",
          border: "1px solid rgba(99, 102, 241, 0.18)",
        }}
      >
        {summaryParagraphs.length > 0 && (
          <div style={{ marginTop: 0, marginBottom: "12px" }}>
            {summaryParagraphs.map((p, idx) => (
              <p
                key={idx}
                style={{
                  marginTop: idx === 0 ? 0 : "8px",
                  marginBottom: idx === summaryParagraphs.length - 1 ? 0 : "8px",
                }}
              >
                {p}
              </p>
            ))}
          </div>
        )}
                    {showMarketPhase && (
              <p style={{ margin: 0, marginBottom: "16px" }}>
                <strong>{labels.currentMarketPhase}:</strong>{" "}
                <span>{localizeMarketContextText(market_phase, reportLanguageCode)}</span>
              </p>
            )}
      {(strengthsList.length > 0 || weaknessesList.length > 0) && (
        <div className="asset-class-context-bridge" style={{ margin: "14px 0 10px" }}>
          <p style={{ margin: 0 }}><strong>{implication.bridgeTitle}</strong></p>
          <p className="muted small" style={{ margin: "4px 0 0", lineHeight: 1.45 }}>{implication.bridgeBody}</p>
        </div>
      )}
      <div
  className="asset-class-context-columns"
  style={{
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
    gap: "16px",
  }}
>
  <div className="asset-class-context-column">
    {strengthsList.length > 0 && (
      <>
        <p style={{ marginBottom: "4px" }}>
          <strong>{implication.strengths}:</strong>
        </p>
        <ul style={{ marginTop: 0, paddingLeft: "18px" }}>
          {strengthsList.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </>
    )}
  </div>

  <div className="asset-class-context-column">
    {weaknessesList.length > 0 && (
      <>
        <p style={{ marginBottom: "4px" }}>
          <strong>{implication.weaknesses}:</strong>
        </p>
        <ul style={{ marginTop: 0, paddingLeft: "18px" }}>
          {weaknessesList.map((item, idx) => (
            <li key={idx} style={idx === weaknessesList.length - 1 ? {breakInside: "avoid", pageBreakInside: "avoid"} : undefined}>
              {item}
              {idx === weaknessesList.length - 1 && confidenceLabel === "low" && (
                <p className="muted small asset-context-source-note" style={{marginTop: 10, marginBottom: 0}}>{labels.marketContextNote}</p>
              )}
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
</div>

        {confidenceLabel === "low" && weaknessesList.length === 0 && (
  <p className="muted small" style={{ marginTop: "12px", marginBottom: 0 }}>
  {labels.marketContextNote}
</p>
        )}
      </div>
    </section>
  );
}
